import { CSSProperties, ElementType } from 'react';
import {
  RiCheckboxCircleFill,
  RiErrorWarningFill,
  RiPauseCircleFill,
  RiTimeFill,
} from '@remixicon/react';
import {
  getQualitySealIconColor,
  qualitySealPropertiesMap,
  QualitySealStatus,
} from '@internal/backstage-plugin-quality-seal-common';
import styles from './QualitySealStatusIcon.module.css';

const qualitySealIconMap: Record<QualitySealStatus, ElementType> = {
  [QualitySealStatus.APPROVED]: RiCheckboxCircleFill,
  [QualitySealStatus.REVIEW_PENDING]: RiTimeFill,
  [QualitySealStatus.UNVERIFIED]: RiErrorWarningFill,
  [QualitySealStatus.INACTIVE]: RiPauseCircleFill,
};

export type QualitySealStatusIconProps = {
  /** The Quality Seal status to show. */
  status: QualitySealStatus;
};

/**
 * Icon for the Quality Seal status of a catalog search result, colored with
 * the status' light or dark theme color. Renders nothing for unknown statuses.
 */
export const QualitySealStatusIcon = ({
  status,
}: QualitySealStatusIconProps) => {
  const statusProperties = qualitySealPropertiesMap[status];
  const Icon = qualitySealIconMap[status];

  if (!statusProperties || !Icon) {
    return null;
  }

  return (
    <Icon
      role="img"
      aria-label={`Quality Seal: ${statusProperties.label}`}
      data-testid={
        status === QualitySealStatus.INACTIVE ? 'InactiveIcon' : undefined
      }
      size="1.25rem"
      className={styles.icon}
      style={
        {
          '--quality-seal-light-color': getQualitySealIconColor(
            statusProperties,
            false,
          ),
          '--quality-seal-dark-color': getQualitySealIconColor(
            statusProperties,
            true,
          ),
        } as CSSProperties
      }
    />
  );
};
